import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import classNames from 'classnames';

export interface DraggableWrapperProps {
  children: ReactNode;
  className?: string;
  dragHandleSelector?: string;
  width?: number;
  height?: number;
  initialPosition?: { x: number; y: number };
  excludeLeftArea?: boolean;
  excludeLeftAreaSelector?: string;
  excludeLeftAreaPadding?: number;
  onDragStart?: () => void;
  onDrag?: (position: { x: number; y: number }) => void;
  onDragEnd?: () => void;
  style?: React.CSSProperties;
}

const DraggableWrapper: React.FC<DraggableWrapperProps> = ({
  children,
  className,
  dragHandleSelector = '.dialer-header',
  width = 300,
  height = 521,
  initialPosition,
  excludeLeftArea = true,
  excludeLeftAreaSelector = '.app-sidebar',
  excludeLeftAreaPadding = 20,
  onDragStart,
  onDrag,
  onDragEnd,
  style,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const boundsRef = useRef<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);

  // Initialize position
  const [position, setPosition] = useState(() => {
    if (initialPosition) {
      return initialPosition;
    }
    // Calculate initial position on mount - ensure it's to the right of sidebar
    if (excludeLeftArea) {
      const sidebarElement = document.querySelector(
        excludeLeftAreaSelector,
      ) as HTMLElement;
      if (sidebarElement) {
        return {
          x: window.innerWidth - 360, // Default to 300px from right edge
          y: window.innerHeight - 700,
        };
      }
    }
    return { x: 260, y: 100 }; // Default position
  });

  // Get sidebar width to exclude it from draggable area
  const getExcludedAreaWidth = useCallback(() => {
    if (!excludeLeftArea) return 0;
    const sidebarElement = document.querySelector(
      excludeLeftAreaSelector,
    ) as HTMLElement;
    if (sidebarElement) {
      const rect = sidebarElement.getBoundingClientRect();
      return rect.width || 240;
    }
    return 240; // Default width if not found
  }, [excludeLeftArea, excludeLeftAreaSelector]);

  // Calculate bounds based on excluded area and actual container size
  const calculateBounds = useCallback(() => {
    const excludedWidth = getExcludedAreaWidth();
    const containerElement = containerRef.current;
    const containerRect = containerElement?.getBoundingClientRect();

    const effectiveWidth = containerRect?.width ?? width;
    const effectiveHeight = containerRect?.height ?? height;

    return {
      left: excludedWidth + (excludeLeftArea ? excludeLeftAreaPadding : 0),
      top: 0,
      right: window.innerWidth - effectiveWidth,
      bottom: window.innerHeight - effectiveHeight,
    };
  }, [
    getExcludedAreaWidth,
    width,
    height,
    excludeLeftArea,
    excludeLeftAreaPadding,
  ]);

  // Update bounds on window resize or sidebar expand/collapse
  useEffect(() => {
    const updateBounds = () => {
      boundsRef.current = calculateBounds();

      // If container is currently positioned to the left of excluded area, move it
      const currentBounds = boundsRef.current;
      if (currentBounds && position.x < currentBounds.left) {
        setPosition((prev) => ({
          ...prev,
          x: currentBounds.left,
        }));
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);

    // Watch for sidebar expand/collapse by observing class changes
    if (excludeLeftArea) {
      const sidebarElement = document.querySelector(excludeLeftAreaSelector);
      if (sidebarElement) {
        const observer = new MutationObserver(() => {
          updateBounds();
        });

        observer.observe(sidebarElement, {
          attributes: true,
          attributeFilter: ['class'],
        });

        // Also update bounds periodically to catch any changes
        const interval = setInterval(updateBounds, 200);

        return () => {
          window.removeEventListener('resize', updateBounds);
          observer.disconnect();
          clearInterval(interval);
        };
      }
    }

    // Fallback: update bounds periodically if sidebar not found
    const interval = setInterval(updateBounds, 200);

    return () => {
      window.removeEventListener('resize', updateBounds);
      clearInterval(interval);
    };
  }, [calculateBounds, position.x, excludeLeftArea, excludeLeftAreaSelector]);

  // Drag functionality
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only allow dragging from specified handle selector
      if ((e.target as HTMLElement).closest(dragHandleSelector)) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
        onDragStart?.();
      }
    },
    [position, dragHandleSelector, onDragStart],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Get current bounds
        const currentBounds = boundsRef.current || calculateBounds();

        // Constrain position within bounds
        const constrainedX = Math.max(
          currentBounds.left,
          Math.min(newX, currentBounds.right),
        );
        const constrainedY = Math.max(
          currentBounds.top,
          Math.min(newY, currentBounds.bottom),
        );

        const newPosition = {
          x: constrainedX,
          y: constrainedY,
        };

        setPosition(newPosition);
        onDrag?.(newPosition);
      }
    },
    [isDragging, dragStart, calculateBounds, onDrag],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [isDragging, onDragEnd]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      role="presentation"
      ref={containerRef}
      className={classNames(className, { dragging: isDragging })}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        ...style,
      }}
      onMouseDown={handleMouseDown}
    >
      {children}
    </div>
  );
};

export default DraggableWrapper;
